import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import {
  onCreateAppointmentNotificationOptimized,
  onUpdateAppointmentNotificationOptimized,
} from "~/api/graphql-custom/subscriptions";
import { useCurrentUser } from "~/auth/UserContext";
import { withErrorReporting } from "~/error-handling/reactQueryErrorUtil";
import { useSubscriptions } from "~/hooks/useSubscriptions";
import { type NotificationCardData } from "~/indigo-components/src/components/NotificationBar/NotificationCard";
import {
  fetchPortalNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  markNotificationAsUnread,
  type PortalNotification,
} from "~/services/notifications";

export type UINotification = NotificationCardData & {
  readAt?: string | null;
};

type UseNotificationsResult = {
  notifications: UINotification[];
  unreadCount: number;
  isLoading: boolean;
  recipientID: string | undefined;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
};

export const notificationKeys = {
  all: ["notifications"] as const,
  infiniteList: (recipientID: string) =>
    [...notificationKeys.all, "infinite-list", recipientID] as const,
  unreadCount: (recipientID: string) =>
    [...notificationKeys.all, "unread-count", recipientID] as const,
};

/**
 * Resolve the recipientID for querying notifications.
 *
 * The notification service stores User.id (the DynamoDB User record ID) as
 * the recipientID for all roles. We must query with the same ID so the GSI
 * lookup matches.
 */
const useRecipientID = (): string | undefined => {
  const { user } = useCurrentUser();
  return user?.id;
};

/**
 * Map backend notification to NotificationCardData for the UI component.
 */
const toCardData = (n: PortalNotification): UINotification => ({
  id: n.id,
  appointmentRecordID: n.appointmentRecordID,
  notificationType: n.notificationType,
  title: n.title,
  body: n.body,
  readAt: n.readAt,
  status: n.readAt ? "READ" : "UNREAD",
  createdAt: n.createdAt ?? n.updatedAt ?? new Date().toISOString(),
});

// ─── useNotifications ──────────────────────────────────────────────────────────

export const useNotifications = (
  enabled: boolean = true,
): UseNotificationsResult => {
  const recipientID = useRecipientID();
  const queryClient = useQueryClient();

  const query = useInfiniteQuery<
    { notifications: PortalNotification[]; nextToken: string | null },
    Error
  >({
    queryKey: notificationKeys.infiniteList(recipientID!), // enabled guards against undefined
    queryFn: ({ pageParam }) =>
      withErrorReporting(
        () =>
          fetchPortalNotifications(
            recipientID!,
            undefined,
            pageParam as string | null | undefined,
          ),
        {
          componentName: "Notifications",
          operationName: "fetch-portal-notifications",
        },
      ),
    getNextPageParam: (lastPage) => lastPage.nextToken ?? undefined,
    initialPageParam: null as string | null,
    enabled: enabled && !!recipientID,
    staleTime: 2 * 60 * 1000, // 2 min
  });

  const unreadCountQuery = useQuery<number, Error>({
    queryKey: notificationKeys.unreadCount(recipientID!),
    queryFn: () =>
      withErrorReporting(
        async () => {
          let unreadCount = 0;
          let nextToken: string | null = null;

          do {
            const page = await fetchPortalNotifications(
              recipientID!,
              100,
              nextToken,
            );
            unreadCount += page.notifications.filter(
              (item) => !item.readAt,
            ).length;
            nextToken = page.nextToken;
          } while (nextToken);

          return unreadCount;
        },
        {
          componentName: "Notifications",
          operationName: "fetch-portal-unread-count",
        },
      ),
    enabled: enabled && !!recipientID,
    staleTime: 2 * 60 * 1000, // 2 min
  });

  // ── Real-time subscriptions ──────────────────────────────────────────────

  // TODO: Invalidating all pages causes every loaded page to refetch when a
  // real-time subscription fires. A lighter approach (e.g. prepending the new
  // notification via setQueryData) would avoid redundant requests when many
  // pages have been loaded. Acceptable for now given typical page counts.
  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: notificationKeys.all,
    });
  }, [queryClient]);

  // Stable ref so the callback doesn't trigger re-subscribe
  const invalidateRef = useRef(invalidate);
  invalidateRef.current = invalidate;

  useSubscriptions({
    queries:
      enabled && recipientID
        ? [
            {
              query: onCreateAppointmentNotificationOptimized as string,
              variables: {
                filter: {
                  recipientID: { eq: recipientID },
                },
              },
              key: "onCreateNotification",
            },
            {
              query: onUpdateAppointmentNotificationOptimized as string,
              variables: {
                filter: {
                  recipientID: { eq: recipientID },
                },
              },
              key: "onUpdateNotification",
            },
          ]
        : [],
    onUpdate: () => invalidateRef.current(),
  });

  // ── Derived data ──────────────────────────────────────────────────────────

  const rawNotifications =
    query.data?.pages.flatMap((page) => page.notifications) ?? [];
  const notifications: UINotification[] = rawNotifications.map(toCardData);

  const unreadCountFromLoadedPages = rawNotifications.filter(
    (n) => !n.readAt,
  ).length;
  const unreadCount = unreadCountQuery.data ?? unreadCountFromLoadedPages;

  return {
    notifications,
    unreadCount,
    isLoading: query.isLoading,
    recipientID,
    hasNextPage: !!query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => query.fetchNextPage(),
  };
};

// ─── useMarkNotificationAsRead ───────────────────────────────────────────────

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) =>
      withErrorReporting(() => markNotificationAsRead(notificationId), {
        componentName: "Notifications",
        operationName: "mark-notification-read",
        mutationType: "update",
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationKeys.all,
      });
    },
  });
};

// ─── useMarkNotificationAsUnread ─────────────────────────────────────────────

export const useMarkNotificationAsUnread = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) =>
      withErrorReporting(() => markNotificationAsUnread(notificationId), {
        componentName: "Notifications",
        operationName: "mark-notification-unread",
        mutationType: "update",
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationKeys.all,
      });
    },
  });
};

// ─── useMarkAllNotificationsAsRead ───────────────────────────────────────────

export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();
  const recipientID = useRecipientID();

  return useMutation({
    mutationFn: () => {
      if (!recipientID) return Promise.resolve();
      return withErrorReporting(() => markAllNotificationsAsRead(recipientID), {
        componentName: "Notifications",
        operationName: "mark-all-notifications-read",
        mutationType: "update",
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationKeys.all,
      });
    },
  });
};
